import { useAdminUserList } from "@/api/services/admin/admin-user";
import { TGetAdminUserListDto } from "@/api/services/admin/admin-user/dtos/get-admin-user-list.dto";
import { useEnum } from "@/api/services/global/enum";
import { IconPlus } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { Table } from "@/components/elements/Table";
import { TableSorting } from "@/components/elements/Table/TableSorting";
import { DATE_FORMAT } from "@/constants/datetime";
import { EAdminUserAccountStatus, EAdminUserStatus } from "@/constants/enum";
import { EMPTY_STR } from "@/constants/string";
import { AdminUserListPagination } from "@/features/system-settings/components/admin-user/AdminUserListPagination";
import { AdminUserStatusIcon } from "@/features/system-settings/components/admin-user/AdminUserStatusIcon";
import { useAdminUserFilterCache } from "@/features/system-settings/providers/AdminUserListFilterCacheProvider";
import { useFillViewportHeight } from "@/hooks/useFillViewportHeight";
import { usePagination } from "@/hooks/usePagination";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { black, text } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { findLabelByValue } from "@/utils/object";
import { getDynamicRoute } from "@/utils/url";
import { Box, Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

const AdminUserListPage = () => {
  const navigate = useNavigate();

  const { filterCache } = useAdminUserFilterCache();
  const pagReq = usePagination();

  const { ref: tableAreaRef, maxHeight: tableMaxHeight } = useFillViewportHeight<HTMLDivElement>({ bottomGap: 120 });
  const { data, isLoading, isFetching } = useAdminUserList({
    ...filterCache,
    sorts: pagReq?.sorts && pagReq?.sorts?.length > 0 ? pagReq?.sorts : undefined,
  });

  const { data: adminUserRoles } = useEnum("EAdminUserRole");
  const items = data?.data ?? [];
  const pag = data?.pagination;

  const columns = useMemo<MRT_ColumnDef<TGetAdminUserListDto>[]>(
    () => [
      {
        accessorKey: "status",
        header: "状態",
        size: 60,
        Header: () => (
          <Typography variant="body14Bold" color={text.primary}>
            状態
          </Typography>
        ),
        Cell: ({ row }) => (
          <AdminUserStatusIcon
            isDisabled={Boolean(row.original?.status === EAdminUserStatus.DISABLED)}
            isPendingActivation={Boolean(row.original?.accountStatus === EAdminUserAccountStatus.INVITATION_SENT)}
          />
        ),
      },
      {
        accessorKey: "fullName",
        header: "担当者名",
        size: 253,
        Header: () => (
          <TableSorting name="fullName">
            <Typography variant="body14Bold" color={text.primary}>
              担当者名
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => <EllipsisText title={row.original?.fullName ?? EMPTY_STR.TEXT} variant="body14" maxWidth={230} />,
      },
      {
        accessorKey: "email",
        header: "メールアドレス",
        size: 253,
        Header: () => (
          <TableSorting name="email">
            <Typography variant="body14Bold" color={text.primary}>
              メールアドレス
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => <EllipsisText title={row.original?.email ?? EMPTY_STR.TEXT} variant="body14" maxWidth={230} />,
      },
      {
        accessorKey: "role",
        header: "担当",
        size: 200,
        Header: () => (
          <TableSorting name="role">
            <Typography variant="body14Bold" color={text.primary}>
              担当
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => {
          const role = row.original?.role;
          return role ? findLabelByValue(adminUserRoles, role) : "-";
        },
      },
      {
        accessorKey: "hasPassword",
        header: "パスワード有無",
        size: 200,
        Header: () => (
          <TableSorting name="hasPassword">
            <Typography variant="body14Bold" color={text.primary}>
              パスワード有無
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => {
          return row.original?.isPasswordRegistered ? "あり" : "なし";
        },
      },
      {
        accessorKey: "createdAt",
        header: "登録日",
        size: 120,
        Header: () => (
          <TableSorting name="createdAt">
            <Typography variant="body14Bold" color={text.primary}>
              登録日
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => {
          const date = row.original?.createdAt;
          return date ? dayjs(date).format(DATE_FORMAT) : EMPTY_STR.TEXT;
        },
      },
    ],
    [adminUserRoles],
  );

  const { table } = useTable({
    columns,
    rows: items,
    onClickRow: (row) => navigate(getDynamicRoute(AppRoutes.systemSettingsAdminUserDetail, { id: row.id })),
    muiTableBodyRowProps: ({ row }) => {
      const isDisabled = row.original?.status === EAdminUserStatus.DISABLED;
      return {
        sx: isDisabled
          ? {
              "& .MuiTableCell-root": {
                color: "rgba(0, 0, 0, 0.3) !important",
                "& > *": {
                  opacity: "0.3 !important",
                },
              },
            }
          : undefined,
      };
    },
  });

  return (
    <Stack gap={2}>
      <Typography variant="body14" color={text.secondary}>
        irohana centerの管理やデータ制作を実施するメンバーを管理できます。いろはなメンバーのみ登録可能です。
      </Typography>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body14Semi" color={black[30]}>
          全 {pag?.total?.toLocaleString() ?? 0} 件中 {pag?.count?.toString().padStart(2, "0") ?? "00"} 件を表示
        </Typography>
        <Button sx={{ paddingX: "16px !important" }} size="sm" onClick={() => navigate(AppRoutes.systemSettingsAdminUserCreate)}>
          <IconPlus fontSize="13px" color="currentColor" />
          <Typography
            sx={{
              ml: "8px",
            }}
            variant="body14Semi"
          >
            管理ユーザーを追加
          </Typography>
        </Button>
      </Stack>

      <Box ref={tableAreaRef}>
        <Table table={table} isLoading={isLoading} isFetching={isFetching} maxHeight={tableMaxHeight} />
      </Box>

      <AdminUserListPagination totalPage={pag?.totalPage} />
    </Stack>
  );
};

export default AdminUserListPage;
